<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import UpgradePage from '../components/UpgradePage.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import PortalSwitcher from 'dashboard/components-next/HelpCenter/PortalSwitcher/PortalSwitcher.vue';
import CreatePortalDialog from 'dashboard/components-next/HelpCenter/PortalSwitcher/CreatePortalDialog.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const isPortalSwitcherOpen = ref(false);
const createPortalDialogRef = ref(null);

const accountId = computed(() => store.getters.getCurrentAccountId);
const portals = computed(() => store.getters['portals/allPortals']);
const isFeatureEnabledonAccount = (id, flag) =>
  store.getters['accounts/isFeatureEnabledonAccount'](id, flag);

const isHelpCenterEnabled = computed(() =>
  isFeatureEnabledonAccount(accountId.value, FEATURE_FLAGS.HELP_CENTER)
);

const selectedPortal = computed(() => {
  const slug =
    route.params.portalSlug || uiSettings.value.last_active_portal_slug;
  if (slug) return store.getters['portals/portalBySlug'](slug);
  return portals.value[0];
});

const defaultPortalLocale = computed(() =>
  selectedPortal.value ? selectedPortal.value.meta?.default_locale : ''
);
const selectedLocaleInPortal = computed(
  () => route.params.locale || defaultPortalLocale.value
);

const selectedPortalSlug = computed(() =>
  selectedPortal.value ? selectedPortal.value.slug : ''
);

const navigationTabs = computed(() => {
  const portalSlug = selectedPortalSlug.value;
  const locale = selectedLocaleInPortal.value;
  if (!portalSlug) return [];

  return [
    {
      label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
      routeName: 'portals_articles_index',
      params: { portalSlug, locale },
      routeNames: [
        'portals_articles_index',
        'portals_articles_new',
        'portals_articles_edit',
      ],
    },
    {
      label: t('SIDEBAR.HELP_CENTER.FAQS'),
      routeName: 'portals_faqs_index',
      params: { portalSlug },
      routeNames: ['portals_faqs_index'],
    },
    {
      label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
      routeName: 'portals_categories_index',
      params: { portalSlug, locale },
      routeNames: [
        'portals_categories_index',
        'portals_categories_articles_index',
        'portals_categories_articles_edit',
      ],
    },
    {
      label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
      routeName: 'portals_settings_index',
      params: { portalSlug },
      routeNames: ['portals_settings_index', 'portals_locales_index'],
    },
  ];
});

const isCurrentTab = tab => tab.routeNames.includes(route.name);

const navigateToTab = tab => {
  router.push({ name: tab.routeName, params: tab.params });
};

const togglePortalSwitcher = () => {
  isPortalSwitcherOpen.value = !isPortalSwitcherOpen.value;
};

const openCreatePortalDialog = () => {
  createPortalDialogRef.value.dialogRef.open();
};

const fetchPortalAndItsCategories = async () => {
  await store.dispatch('portals/index');
  const selectedPortalParam = {
    portalSlug: selectedPortalSlug.value,
    locale: selectedLocaleInPortal.value,
  };
  store.dispatch('portals/show', selectedPortalParam);
  store.dispatch('categories/index', selectedPortalParam);
  store.dispatch('agents/get');
};

onMounted(() => fetchPortalAndItsCategories());

watch(
  () => route.params.portalSlug,
  newSlug => {
    if (newSlug && newSlug !== uiSettings.value.last_active_portal_slug) {
      updateUISettings({
        last_active_portal_slug: newSlug,
        last_active_locale_code: selectedLocaleInPortal.value,
      });
    }
  }
);
</script>

<template>
  <div class="flex flex-col flex-grow-0 w-full h-full min-h-0 app-wrapper">
    <header
      v-if="isHelpCenterEnabled && navigationTabs.length"
      class="shrink-0 border-b border-n-weak bg-n-background"
    >
      <div class="w-full px-10 mx-auto">
        <div class="flex items-baseline justify-between gap-4 pt-8">
          <div class="flex items-center gap-2 min-w-0">
            <h1
              class="text-3xl font-semibold leading-none tracking-tight text-n-slate-12"
            >
              {{ t('SIDEBAR.HELP_CENTER.TITLE') }}
            </h1>
            <div
              v-if="selectedPortal && portals.length > 1"
              class="relative group"
            >
              <OnClickOutside @trigger="isPortalSwitcherOpen = false">
                <Button
                  :label="selectedPortal.name"
                  icon="i-lucide-chevron-down"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  trailing-icon
                  class="!w-fit max-w-56 text-n-slate-11"
                  @click="togglePortalSwitcher"
                />
                <PortalSwitcher
                  v-if="isPortalSwitcherOpen"
                  class="absolute ltr:left-0 rtl:right-0 top-9"
                  @close="isPortalSwitcherOpen = false"
                  @create-portal="openCreatePortalDialog"
                />
              </OnClickOutside>
              <CreatePortalDialog ref="createPortalDialogRef" />
            </div>
          </div>
          <p class="hidden text-sm whitespace-nowrap text-n-slate-10 sm:block">
            {{ t('HELP_CENTER.HEADER.DESCRIPTION') }}
          </p>
        </div>
        <nav
          class="flex gap-6 mt-6 overflow-x-auto"
          :aria-label="t('SIDEBAR.HELP_CENTER.TITLE')"
        >
          <button
            v-for="tab in navigationTabs"
            :key="tab.routeName"
            type="button"
            :aria-current="isCurrentTab(tab) ? 'page' : undefined"
            class="shrink-0 px-px pb-3 text-sm font-semibold border-b-2 border-solid rounded-none transition-colors"
            :class="
              isCurrentTab(tab)
                ? 'border-violet-500 text-n-slate-12'
                : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
            "
            @click="navigateToTab(tab)"
          >
            {{ tab.label }}
          </button>
        </nav>
      </div>
    </header>
    <section
      v-if="isHelpCenterEnabled"
      class="flex flex-1 min-h-0 px-0 pt-8 overflow-hidden bg-n-background"
    >
      <router-view />
    </section>
    <UpgradePage v-else />
  </div>
</template>
